<script>
import CustomerEditForm from '@app/components/CustomerEditForm.vue';
import APIStore from '@app/services/API/Store';

export default {
  components: {
    CustomerEditForm,
  },
  props: {
    customer: {
      type: Object,
      required: true,
    },
    titleTag: {
      type: [String, Object],
      default: 'h2',
    },
  },
  data: () => ({
    isEditing: false,
    hasActiveMembership: false,
    isCheckingMembership: true,
    customFieldsModalActive: false,
    CUSTOM_FIELD_TRUNCATE: 40,
  }),
  computed: {
    authStore() {
      return this.$store.getters['auth/store'];
    },
    customCustomerFields() {
      const opts = this.authStore && this.authStore.custom_fields_options;
      if (!opts || !opts.enabled || !(opts.fields || []).length) return [];
      const data = (this.customer && this.customer.custom_fields_data) || [];
      const byName = data.reduce((acc, a) => { acc[a.name] = a; return acc; }, {});
      return opts.fields.map((f) => {
        const found = byName[f.name];
        return {
          name: f.name,
          label: f.label,
          type: f.type,
          value: found ? found.value : '',
        };
      });
    },
    hasAnyCustomerFieldValue() {
      return this.customCustomerFields.some((a) => a.value !== '' && a.value !== null && a.value !== undefined);
    },
  },
  created() {
    this.checkMembershipStatus();
  },
  methods: {
    formatCustomFieldValue(answer) {
      if (answer.type === 'checkbox' || answer.value === 'true' || answer.value === 'false') {
        return answer.value === 'true' || answer.value === true ? 'Oui' : 'Non';
      }
      return answer.value;
    },
    truncateValue(answer) {
      const v = this.formatCustomFieldValue(answer);
      if (typeof v !== 'string') return v;
      if (v.length <= this.CUSTOM_FIELD_TRUNCATE) return v;
      return `${v.slice(0, this.CUSTOM_FIELD_TRUNCATE)}…`;
    },
    isTruncated(answer) {
      const v = this.formatCustomFieldValue(answer);
      return typeof v === 'string' && v.length > this.CUSTOM_FIELD_TRUNCATE;
    },
    async checkMembershipStatus() {
      try {
        const response = await APIStore.checkCustomerMemberships(
          this.authStore.uuid,
          this.customer.uuid,
        );
        this.hasActiveMembership = response?.has_active_membership ?? false;
      } catch (error) {
        console.warn('Failed to check customer memberships:', error);
      } finally {
        this.isCheckingMembership = false;
      }
    },
    updateCustomer(data) {
      return this.$store.dispatch('customers/update', {
        storeUUID: this.authStore.uuid,
        customerUUID: this.customer.uuid,
        data,
      });
    },
    async deleteCustomer() {
      const loader = this.$buefy.loading.open();

      let hasCancelAtPeriodEnd = false;

      try {
        const response = await APIStore.checkCustomerMemberships(
          this.authStore.uuid,
          this.customer.uuid,
        );
        hasCancelAtPeriodEnd = response?.has_cancel_at_period_end_membership ?? false;
      } catch (error) {
        console.warn('Failed to check customer memberships:', error);
      } finally {
        loader.close();
      }

      const confirmAction = () => {
        this.$buefy.dialog.prompt({
          message: `
            <p>
              Entrez votre mot de passe pour confirmer cette action.
            </p>
          `,
          focusOn: 'cancel',
          inputAttrs: { required: true, type: 'password' },
          onConfirm: (password) => {
            if (password) {
              const loader2 = this.$buefy.loading.open();
              this.$store.dispatch('customers/delete', {
                storeUUID: this.authStore.uuid,
                customerUUID: this.customer.uuid,
                password,
              })
                .then(() => this.$showMessage.success())
                .catch((error) => this.$errorHandlers.axios(error))
                .finally(() => loader2.close());
            }
          },
        });
      };

      if (hasCancelAtPeriodEnd) {
        this.$buefy.dialog.confirm({
          title: 'Attention : Abonnement en cours',
          message: `
            <p class="mb-3">
              <strong>Cet apprenant a un abonnement actif qui se termine à la fin de la période.</strong>
            </p>
            <p class="mb-3">
              Si vous supprimez cet apprenant maintenant, l'abonnement sera également supprimé et l'accès sera immédiatement révoqué, même si la période n'est pas encore terminée.
            </p>
            <p>
              Cette action supprimera définitivement cet apprenant et ses accès.
            </p>
            <p class="mt-3 has-text-weight-bold">
              Voulez-vous vraiment continuer ?
            </p>
          `,
          focusOn: 'cancel',
          confirmText: 'Supprimer quand même',
          cancelText: 'Annuler',
          type: 'is-warning',
          onConfirm: () => {
            this.$buefy.dialog.confirm({
              message: `
                ${this.authStore && this.authStore.plan === 'PERCENT' ? `
                  <p class="mb-3">
                    Après suppression, cet apprenant sera quand même comptabilisé dans les 70 autorisés par votre forfait actuel.
                  </p>
                ` : ''}

                <p>
                  Cette action supprimera définitivement cet apprenant et ses accès.
                </p>

                <p class="mt-3 has-text-weight-bold">
                  Voulez-vous vraiment continuer ?
                </p>
              `,
              focusOn: 'cancel',
              confirmText: 'Supprimer',
              type: 'is-danger',
              onConfirm: confirmAction,
            });
          },
        });
      } else {
        this.$buefy.dialog.confirm({
          message: `
            ${this.authStore && this.authStore.plan === 'PERCENT' ? `
              <p class="mb-3">
                Après suppression, cet apprenant sera quand même comptabilisé dans les 70 autorisés par votre forfait actuel.
              </p>
            ` : ''}

            <p class="mb-3">
              En supprimant cet apprenant, il n'aura <strong>plus accès à son espace apprenant et à ses formations</strong>.
              Les données le concernant seront <strong>définitivement supprimées</strong>.
            </p>

            <p class="mt-3 has-text-weight-bold">
              Voulez-vous vraiment continuer ?
            </p>
          `,
          focusOn: 'cancel',
          confirmText: 'Supprimer',
          type: 'is-danger',
          onConfirm: confirmAction,
        });
      }
    },
  },
};
</script>

<template>
  <section>
    <div
      v-if="customer.status == 'DISABLED'"
      class="box is-danger content"
    >
      <p>
        <b-icon icon="exclamation-triangle" class="has-text-black mr-3" />
        <strong>Accès bloqué pour cet apprenant</strong> :
        la limite de votre forfait est atteinte.
      </p>
    </div>
    <div class="card">
      <div class="card-image">
        <figure class="image is-2by1 bbottom-grey-light">
          <img
            v-if="customer.picture"
            class="h-full w-full ofit-contain has-background-dark"
            :src="`${$env.apiResourcesURL}/${customer.picture}`"
            :alt="`${customer.firstname} ${customer.lastname}`"
          >
          <div v-else class="pabsolute-0 flex-center-center">
            <b-icon icon="user" class="is-size-1" />
          </div>
        </figure>
      </div>
      <div class="card-content p-3">
        <div class="has-text-centered break-words">
          <component
            :is="titleTag"
            class="is-size-5 has-text-weight-medium has-text-black"
          >
            {{ customer.firstname }}
            <br>
            <template v-if="!!customer.lastname">
              {{ customer.lastname }}
            </template>
            <template v-else>
              (aucun nom renseigné)
            </template>
          </component>
          <div class="mt-3 is-size-8 has-background-light bradius-4">
            <div class="w-full bbottom-grey-light p-2">
              <div class="is-flex w-full is-justify-content-space-between">
                <div class="has-text-left">
                  <strong>Inscrit</strong>

                  <template v-if="customer.utm_data">
                    <b-tooltip class="w-full" multilined>
                      <template #content>
                        <p class="has-text-left" v-t="'customer.via_utm_text'" />
                        <hr class="my-2" style="background-color: rgba(255, 255, 255, 0.2)">
                        <ul class="has-text-left">
                          <template v-for="(utm, utmKey) in customer.utm_data">
                            <li v-if="utm" :key="utmKey">
                              - {{ utmKey }} : {{ utm }}
                            </li>
                          </template>
                        </ul>
                      </template>
                      Via un lien UTM
                    </b-tooltip>
                  </template>
                </div>
                <b-tooltip>
                  <template #content>
                    Inscription le <br>
                    {{ customer.created_at | momentFromUTC | moment('DD/MM/YY [à] HH:mm') }}
                  </template>
                  {{ customer.created_at | momentFromUTC | moment('from') }}
                </b-tooltip>
              </div>
            </div>
            <div class="w-full p-2" :active="!!customer.last_login_at">
              <template v-if="customer.last_login_at">
                <div class="is-flex w-full is-justify-content-space-between">
                  <strong>Connecté</strong>

                  <b-tooltip :active="!!customer.last_login_at">
                    <template #content>
                      Dernière connexion le <br>
                      {{ customer.last_login_at | momentFromUTC | moment('DD/MM/YY [à] HH:mm') }}
                    </template>
                    {{ customer.last_login_at | momentFromUTC | moment('from') }}
                  </b-tooltip>
                </div>
              </template>
              <template v-else>
                Jamais connecté
              </template>
            </div>
          </div>
          <div class="mt-3">
            <b-button
              type="is-primary"
              expanded
              @click="isEditing = true"
            >
              Modifier les informations
            </b-button>
            <b-tooltip
              class="w-full"
              :active="hasActiveMembership"
              label="Impossible de supprimer un apprenant avec un abonnement actif"
              type="is-dark"
              position="is-top"
              multilined
            >
              <b-button
                class="mt-2 w-full"
                type="is-danger"
                expanded
                :disabled="hasActiveMembership || isCheckingMembership"
                :loading="isCheckingMembership"
                @click="deleteCustomer"
              >
                Supprimer l'apprenant
              </b-button>
            </b-tooltip>
          </div>
        </div>
      </div>
      <div class="card-content px-0 pt-3 pb-1">
        <table class="table is-fullwidth is-size-8" style="table-layout: fixed;">
          <tbody>
            <tr>
              <td colspan="2" class="has-text-weight-medium">
                <div class="flex-between-center">
                  <div class="has-text-left">
                    E-mail :
                  </div>
                  <div class="has-text-right break-words">
                    <a
                      href="#"
                      @click.prevent
                      v-clipboard:copy="customer.email"
                      v-clipboard:success="() => $copySuccess()"
                      v-clipboard:error="() => $copyError()">
                      {{ customer.email }}
                    </a>
                  </div>
                </div>
              </td>
            </tr>
            <tr>
              <td class="has-text-weight-medium w-50p">
                E-mails marketing :
              </td>
              <td class="has-text-right break-words">
                <template v-if="customer.accept_marketing">
                  Acceptés
                </template>
                <template v-else>
                  Refusés
                </template>
              </td>
            </tr>
            <tr>
              <td class="has-text-weight-medium w-50p">
                Téléphone :
              </td>
              <td class="has-text-right break-words">
                <a v-if="!!customer.phone" :href="`tel:${customer.phone}`">
                  {{ customer.phone }}
                </a>
                <template v-else>
                  ---
                </template>
              </td>
            </tr>
            <tr v-for="answer in customCustomerFields" :key="answer.name">
              <td class="has-text-weight-medium w-50p">
                {{ answer.label }} :
              </td>
              <td class="has-text-right break-words">
                <template v-if="answer.value !== '' && answer.value !== null && answer.value !== undefined">
                  {{ truncateValue(answer) }}
                </template>
                <template v-else>
                  ---
                </template>
              </td>
            </tr>
            <tr v-if="hasAnyCustomerFieldValue">
              <td colspan="2" class="has-text-centered">
                <b-button
                  type="is-text"
                  size="is-small"
                  icon-left="eye"
                  @click="customFieldsModalActive = true"
                >
                  Voir plus
                </b-button>
              </td>
            </tr>
            <!-- <tr>
              <td class="has-text-weight-medium w-50p">
                Adresse :
              </td>
              <td class="has-text-right break-words">
                <template v-if="!!customer.billing_address">
                  {{ customer.billing_address.address_line_1 }}
                  <template v-if="!!customer.billing_address.address_line_2">
                    {{ customer.billing_address.address_line_2 }}
                  </template>
                </template>
                <template v-else>
                  ---
                </template>
              </td>
            </tr> -->
          </tbody>
        </table>
      </div>
    </div>

    <b-modal v-if="isEditing" :active.sync="isEditing" has-modal-card>
      <CustomerEditForm
        :api="updateCustomer"
        :customer="customer"
        @done="isEditing = false"
      />
    </b-modal>

    <b-modal v-if="customFieldsModalActive" :active.sync="customFieldsModalActive" has-modal-card>
      <div class="modal-card" style="max-width: 640px; width: 100%;">
        <header class="modal-card-head">
          <p class="modal-card-title">Réponses aux questions personnalisées</p>
        </header>
        <section class="modal-card-body">
          <table class="table is-fullwidth">
            <tbody>
              <tr v-for="answer in customCustomerFields" :key="answer.name">
                <td class="has-text-weight-medium" style="vertical-align: top;">
                  {{ answer.label }}
                </td>
                <td class="break-words" style="white-space: pre-wrap;">
                  <template v-if="answer.value !== '' && answer.value !== null && answer.value !== undefined">
                    {{ formatCustomFieldValue(answer) }}
                  </template>
                  <template v-else>
                    ---
                  </template>
                </td>
              </tr>
            </tbody>
          </table>
        </section>
        <footer class="modal-card-foot is-justify-content-flex-end">
          <b-button @click="customFieldsModalActive = false">Fermer</b-button>
        </footer>
      </div>
    </b-modal>
  </section>
</template>
